import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumericInput from "@/components/NumericInput";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardCheck, Loader2, Search, AlertTriangle, Download, Upload, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

// Stok sayımı — preview (DB YAZMAZ) → apply (sadece fark kadar movement + $inc)
export default function StockCountDialog({ open, onOpenChange, warehouse, onDone }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({}); // key -> counted string
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [excelReview, setExcelReview] = useState(null); // {count_id, items, counts}
  const [excelBusy, setExcelBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !warehouse) return;
    setCounts({}); setQ(""); setReviewing(false); setExcelReview(null);
    setLoading(true);
    api.get(`/warehouses/${warehouse.id}/stock-count/preview`)
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, warehouse?.id]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter((r) => matchesAll(ql, [r.product_name, r.variant_sku, r.variant_label, r.barcode]));
  }, [rows, q]);

  const stats = useMemo(() => {
    let z = 0, pos = 0, neg = 0, changed = 0;
    for (const r of rows) {
      const key = `${r.product_id}|${r.variant_id || ""}`;
      const c = counts[key];
      if (c === undefined || c === "") continue;
      const counted = Number(c); const sys = Number(r.system_qty);
      if (Number.isNaN(counted) || counted < 0) continue;
      const d = counted - sys;
      if (Math.abs(d) < 1e-9) z++;
      else if (d > 0) { pos++; changed++; }
      else { neg++; changed++; }
    }
    return { total: rows.length, entered: z + pos + neg, zero: z, positive: pos, negative: neg, changed };
  }, [rows, counts]);

  const apply = async () => {
    const items = [];
    for (const r of rows) {
      const key = `${r.product_id}|${r.variant_id || ""}`;
      const c = counts[key];
      if (c === undefined || c === "") continue;
      const counted = Number(c);
      if (Number.isNaN(counted) || counted < 0) continue;
      items.push({ product_id: r.product_id, variant_id: r.variant_id || "", counted_qty: counted });
    }
    if (items.length === 0) return toast.error("Sayım girilmemiş");
    try {
      setBusy(true);
      const count_id = crypto.randomUUID();
      const { data } = await api.post(`/warehouses/${warehouse.id}/stock-count/apply`, { count_id, items });
      toast.success(`Sayım uygulandı — ${data.applied} satır güncellendi`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Excel hızlı düzeltme — indir / yükle / önizle / onayla (apply mevcut canonical endpoint)
  const downloadExcel = async () => {
    try {
      const { data } = await api.get(`/warehouses/${warehouse.id}/stock-count/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sayim_${(warehouse?.name || "depo").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadExcel = async (f) => {
    if (!f) return;
    setExcelBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/warehouses/${warehouse.id}/stock-count/import-preview`, fd);
      setExcelReview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExcelBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const applyExcel = async () => {
    if (!excelReview) return;
    const items = (excelReview.items || [])
      .filter((it) => it.status === "ok")
      .map((it) => ({ product_id: it.product_id, variant_id: it.variant_id || "", counted_qty: it.counted_qty }));
    if (!items.length) return toast.error("Uygulanabilir satır yok");
    setBusy(true);
    try {
      const { data } = await api.post(`/warehouses/${warehouse.id}/stock-count/apply`, { count_id: excelReview.count_id, items });
      toast.success(`Sayım uygulandı — ${data.applied} satır güncellendi (+${data.positive} / −${data.negative})`);
      setExcelReview(null);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const th = "text-[10px] uppercase tracking-widest text-slate-500 font-mono";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-6xl max-h-[92vh] flex flex-col p-0" data-testid="stock-count-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <ClipboardCheck className="w-4 h-4" /> Stok Sayımı — {warehouse?.name}
            <span className="flex items-center gap-1.5 ml-auto">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="sc-excel-file"
                onChange={(e) => uploadExcel(e.target.files?.[0])} />
              <Button variant="outline" size="sm" onClick={downloadExcel} data-testid="sc-excel-download"
                className="rounded-sm text-[11px] border-slate-700 h-7">
                <Download className="w-3.5 h-3.5 mr-1" /> {rows.length === 0 ? "Boş Şablon İndir" : "Excel İndir"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={excelBusy}
                data-testid="sc-excel-upload" className="rounded-sm text-[11px] border-slate-700 h-7">
                {excelBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                Excel Yükle
              </Button>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-3 overflow-y-auto flex-1">
          {excelReview ? (
            <div className="space-y-3" data-testid="sc-excel-review">
              <div className="rounded-sm border border-cyan-500/40 bg-cyan-500/5 p-3 grid grid-cols-3 gap-2 text-xs font-mono">
                <div><div className="text-[10px] uppercase text-slate-500">Toplam Satır</div><div className="text-lg font-bold">{excelReview.counts?.total || 0}</div></div>
                <div><div className="text-[10px] uppercase text-emerald-400">Uygulanabilir</div><div className="text-lg font-bold text-emerald-400">{excelReview.counts?.ok || 0}</div></div>
                <div><div className="text-[10px] uppercase text-rose-400">Hatalı</div><div className="text-lg font-bold text-rose-400">{excelReview.counts?.error || 0}</div></div>
              </div>
              <div className="border border-slate-800 rounded-sm overflow-hidden">
                <div className="max-h-[55vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800 hover:bg-transparent">
                        <TableHead className={th}>Satır</TableHead>
                        <TableHead className={th}>Ürün</TableHead>
                        <TableHead className={th}>SKU</TableHead>
                        <TableHead className={`${th} text-right`}>Sistem Stok</TableHead>
                        <TableHead className={`${th} text-right`}>Sayım</TableHead>
                        <TableHead className={`${th} text-right`}>Fark</TableHead>
                        <TableHead className={th}>Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(excelReview.items || []).map((it) => (
                        <TableRow key={it.row} className="border-slate-800" data-testid={`sc-excel-row-${it.row}`}>
                          <TableCell className="text-[11px] font-mono text-slate-500">{it.row}</TableCell>
                          <TableCell className="text-xs text-slate-200">{it.product_name}</TableCell>
                          <TableCell className="text-[11px] font-mono text-slate-400">{it.variant_sku || "—"}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{it.system_qty}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{it.counted_qty ?? "—"}</TableCell>
                          <TableCell className={`text-right font-mono text-xs ${it.diff == null ? "text-slate-600" : it.diff === 0 ? "text-slate-400" : it.diff > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                            {it.diff == null ? "—" : (it.diff > 0 ? "+" : "") + it.diff}
                          </TableCell>
                          <TableCell className="text-[11px]">
                            {it.status === "ok"
                              ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Uygun</span>
                              : <span className="text-rose-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {it.reason}</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 pt-2">
                <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-500" /> Yalnızca uygun satırlar uygulanır; onaylamadan stok değişmez.
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setExcelReview(null)} className="rounded-sm text-xs border-slate-700" data-testid="sc-excel-back">Vazgeç</Button>
                  <Button onClick={applyExcel} disabled={(busy || !(excelReview.counts?.ok > 0)) || !(can("warehouse", "edit"))} title={!(can("warehouse", "edit")) ? "Sayım uygulama yetkiniz yok (warehouse.edit)" : undefined}
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-xs uppercase font-semibold" data-testid="sc-excel-apply">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5 mr-1" />}
                    Onayla ve Uygula ({excelReview.counts?.ok || 0} satır)
                  </Button>
                </div>
              </div>
            </div>
          ) : (
          <>
          {reviewing && (
            <div className="rounded-sm border border-cyan-500/40 bg-cyan-500/5 p-3 grid grid-cols-4 gap-2 text-xs font-mono" data-testid="sc-preview-summary">
              <div><div className="text-[10px] uppercase text-slate-500">Toplam Kalem</div><div className="text-lg font-bold">{stats.total}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Farksız</div><div className="text-lg font-bold">{stats.zero}</div></div>
              <div><div className="text-[10px] uppercase text-emerald-400">Fazla (+)</div><div className="text-lg font-bold text-emerald-400">{stats.positive}</div></div>
              <div><div className="text-[10px] uppercase text-rose-400">Eksik (−)</div><div className="text-lg font-bold text-rose-400">{stats.negative}</div></div>
            </div>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün / SKU / barkod filtrele" className="bg-slate-950 border-slate-700 rounded-sm pl-8 max-w-md" data-testid="sc-filter" />
          </div>
          <div className="border border-slate-800 rounded-sm overflow-hidden">
            <div className="max-h-[55vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className={th}>Ürün</TableHead>
                    <TableHead className={th}>SKU</TableHead>
                    <TableHead className={th}>Varyant</TableHead>
                    <TableHead className={th}>Barkod</TableHead>
                    <TableHead className={`${th} text-right`}>Sistem</TableHead>
                    <TableHead className={`${th} text-right`}>Sayılan</TableHead>
                    <TableHead className={`${th} text-right`}>Fark</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow className="border-slate-800"><TableCell colSpan={7} className="text-center py-8"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow className="border-slate-800"><TableCell colSpan={7} className="text-center text-xs text-slate-500 py-8">Sonuç yok</TableCell></TableRow>
                  ) : filtered.map((r) => {
                    const key = `${r.product_id}|${r.variant_id || ""}`;
                    const c = counts[key];
                    const counted = c === "" || c === undefined ? null : Number(c);
                    const delta = counted !== null && !Number.isNaN(counted) ? counted - Number(r.system_qty) : null;
                    return (
                      <TableRow key={key} className="border-slate-800" data-testid={`sc-row-${r.product_id}-${r.variant_id || 'none'}`}>
                        <TableCell className="text-xs text-slate-200">{r.product_name}</TableCell>
                        <TableCell className="text-[11px] font-mono text-slate-400">{r.variant_sku || r.product_code || "—"}</TableCell>
                        <TableCell className="text-[11px] text-slate-300">{r.variant_label || "—"}</TableCell>
                        <TableCell className="font-mono text-[11px] text-slate-500">{r.barcode || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{r.system_qty}</TableCell>
                        <TableCell className="text-right">
                          <NumericInput
                            data-testid={`sc-counted-${r.product_id}-${r.variant_id || 'none'}`}
                            value={c ?? ""} onChange={(v) => setCounts((s) => ({ ...s, [key]: v }))}
                            min={0} step="1" className="bg-slate-950 border-slate-700 rounded-sm text-sm w-24 ml-auto" disabled={reviewing || busy}
                          />
                        </TableCell>
                        <TableCell className={`text-right font-mono text-xs ${delta === null ? "text-slate-600" : delta === 0 ? "text-slate-400" : delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          {delta === null ? "—" : (delta > 0 ? "+" : "") + delta}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-500" /> Sayımı onaylamadan hiçbir stok değişmez.
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm text-xs border-slate-700">İptal</Button>
              {!reviewing ? (
                <Button onClick={() => setReviewing(true)} disabled={stats.entered === 0} className="rounded-sm bg-cyan-600 hover:bg-cyan-500 text-xs uppercase font-semibold" data-testid="sc-preview">
                  Önizleme ({stats.entered} kalem)
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setReviewing(false)} className="rounded-sm text-xs border-slate-700">Geri</Button>
                  <Button onClick={apply} disabled={(busy || stats.changed === 0) || !(can("warehouse", "edit"))} title={!(can("warehouse", "edit")) ? "Sayım uygulama yetkiniz yok (warehouse.edit)" : undefined} className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-xs uppercase font-semibold" data-testid="sc-apply">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5 mr-1" />}
                    Sayımı Onayla ({stats.changed} değişiklik)
                  </Button>
                </>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
